import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { VideoEvent, VideoMetrics } from '../../../modules/room/video-player/video-player.component';

export interface VideoSession {
    videoUrl: string;
    sessionId: string;
    startTime: number;
    endTime?: number;
    events: VideoEvent[];
    metrics: VideoMetrics;
    participantId?: string;
    topicId?: string;
}

export interface VideoAnalytics {
    totalSessions: number;
    averageWatchTime: number;
    averageCompletionRate: number;
    totalEvents: number;
    eventBreakdown: { [key: string]: number };
    engagementHeatmap: { time: number; interactions: number }[];
}

@Injectable({
    providedIn: 'root',
})
export class VideoTrackingService {
    private currentSession: VideoSession | null = null;
    private sessions: VideoSession[] = [];
    private sessionSubject = new BehaviorSubject<VideoSession | null>(null);
    private analyticsSubject = new BehaviorSubject<VideoAnalytics | null>(null);

    public session$ = this.sessionSubject.asObservable();
    public analytics$ = this.analyticsSubject.asObservable();

    constructor() {
        // No localStorage needed - participants don't return
    }

    startSession(
        videoUrl: string,
        participantId?: string,
        topicId?: string,
    ): string {
        const sessionId = this.generateSessionId();
        
        this.currentSession = {
            videoUrl,
            sessionId,
            startTime: Date.now(),
            events: [],
            metrics: {
                totalWatchTime: 0,
                completionPercentage: 0,
                playCount: 0,
                pauseCount: 0,
                seekCount: 0,
                averagePlaybackRate: 1,
                bufferingEvents: 0,
                lastPosition: 0,
            },
            participantId,
            topicId,
        };

        this.sessionSubject.next(this.currentSession);
        console.log(`${this.sessionLabel} tracking session started:`, sessionId);
        return sessionId;
    }

    endSession(): void {
        if (this.currentSession) {
            this.currentSession.endTime = Date.now();
            this.sessions.push(this.currentSession);
            this.updateAnalytics();
            console.log(
                `${this.sessionLabel} tracking session ended:`,
                this.currentSession.sessionId,
            );
            this.currentSession = null;
            this.sessionSubject.next(null);
        }
    }

    trackEvent(event: VideoEvent): void {
        if (!this.currentSession) {
            console.warn('No active video session to track event');
            return;
        }

        this.currentSession.events.push(event);
        
        // Log significant events
        if (['play', 'pause', 'ended', 'error', 'seek'].includes(event.type)) {
            console.log(`Video event tracked: ${event.type}`, event.data);
        }

        // Update session with latest data
        this.sessionSubject.next(this.currentSession);
    }

    updateMetrics(metrics: VideoMetrics): void {
        if (!this.currentSession) {
            console.warn('No active video session to update metrics');
            return;
        }

        this.currentSession.metrics = metrics;
        this.sessionSubject.next(this.currentSession);
    }

    getSessionById(sessionId: string): VideoSession | undefined {
        return this.sessions.find((s) => s.sessionId === sessionId);
    }

    getSessionsByParticipant(participantId: string): VideoSession[] {
        return this.sessions.filter((s) => s.participantId === participantId);
    }

    getSessionsByTopic(topicId: string): VideoSession[] {
        return this.sessions.filter((s) => s.topicId === topicId);
    }

    getSessionsByVideo(videoUrl: string): VideoSession[] {
        return this.sessions.filter((s) => s.videoUrl === videoUrl);
    }

    getCurrentSession(): VideoSession | null {
        return this.currentSession;
    }

    getAllSessions(): VideoSession[] {
        return [...this.sessions];
    }

    generateAnalytics(sessions?: VideoSession[]): VideoAnalytics {
        const targetSessions = sessions || this.sessions;
        
        if (targetSessions.length === 0) {
            return {
                totalSessions: 0,
                averageWatchTime: 0,
                averageCompletionRate: 0,
                totalEvents: 0,
                eventBreakdown: {},
                engagementHeatmap: [],
            };
        }

        const eventBreakdown: { [key: string]: number } = {};
        const heatmapData: { [key: number]: number } = {};
        let totalWatchTime = 0;
        let totalCompletionRate = 0;
        let totalEvents = 0;

        targetSessions.forEach((session) => {
            totalWatchTime += session.metrics.totalWatchTime;
            totalCompletionRate += session.metrics.completionPercentage;
            totalEvents += session.events.length;

            session.events.forEach((event) => {
                // Count event types
                eventBreakdown[event.type] = (eventBreakdown[event.type] || 0) + 1;

                // Build heatmap for seek and interaction events
                if (['seek', 'play', 'pause'].includes(event.type) && event.data?.currentTime) {
                    const timeSlot = Math.floor(event.data.currentTime / 10) * 10;
                    heatmapData[timeSlot] = (heatmapData[timeSlot] || 0) + 1;
                }
            });
        });

        const engagementHeatmap = Object.entries(heatmapData)
            .map(([time, interactions]) => ({
                time: parseInt(time),
                interactions,
            }))
            .sort((a, b) => a.time - b.time);

        const analytics: VideoAnalytics = {
            totalSessions: targetSessions.length,
            averageWatchTime: totalWatchTime / targetSessions.length,
            averageCompletionRate: totalCompletionRate / targetSessions.length,
            totalEvents,
            eventBreakdown,
            engagementHeatmap,
        };

        this.analyticsSubject.next(analytics);
        return analytics;
    }

    exportSessionData(sessionId?: string): string {
        const data = sessionId
            ? this.getSessionById(sessionId)
            : {
                  sessions: this.sessions,
                  analytics: this.generateAnalytics(),
              };

        return JSON.stringify(data, null, 2);
    }

    clearSessions(): void {
        this.sessions = [];
        this.currentSession = null;
        this.sessionSubject.next(null);
        this.analyticsSubject.next(null);
    }

    /**
     * Session id prefix. Overridden by AudioTrackingService so audio sessions are
     * identifiable in the stored analytics and in the console.
     */
    protected sessionKind = 'video';

    /**
     * Capitalised form used in the log lines. Kept separate from sessionKind so
     * the video wording stays byte-identical: log-based metrics in this project
     * match on the exact console string, and lowercasing it would silently stop
     * them matching.
     */
    private get sessionLabel(): string {
        return (
            this.sessionKind.charAt(0).toUpperCase() + this.sessionKind.slice(1)
        );
    }

    protected generateSessionId(): string {
        return `${this.sessionKind}_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private updateAnalytics(): void {
        if (this.sessions.length > 0) {
            this.generateAnalytics();
        }
    }
}